// SPDX-License-Identifier: BUSL-1.1

/**
 * Organization context for multi-tenancy support.
 *
 * Provides current organization state and switching capability to all components.
 *
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from '@/lib/api-client';
import { LoadingScreen } from '@/components/loading-screen';
import { showApiError, showError, showSuccess } from '@/lib/toast';
import { fetchAndStoreBearerJwt, setActiveOrganization } from '@/lib/auth';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'paused' | 'deleted';
  membershipRole?: 'owner' | 'admin' | 'member' | 'viewer' | null;
}

interface OrgContextValue {
  organizations: Organization[];
  currentOrg: Organization | null;
  isLoading: boolean;
  error: string | null;
  switchOrg: (orgId: string) => void;
}

const noop = () => {};

const OrgContext = createContext<OrgContextValue>({
  organizations: [],
  currentOrg: null,
  isLoading: true,
  error: null,
  switchOrg: noop,
});

export function useOrg() {
  return useContext(OrgContext);
}

/** Alias for useOrg() — preferred hook name for components that need current org. */
export { useOrg as useCurrentOrg };

const STORAGE_KEY = 'ceo-org-id';

interface ApiOrgsResponse {
  data: Organization[];
}

/**
 * Organization context provider.
 *
 * Loads user's organizations on mount and provides switching capability.
 * Remembers the last selected org in localStorage.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load organizations on mount
  useEffect(() => {
    async function loadOrgs() {
      try {
        const response = await apiFetch<ApiOrgsResponse>('/orgs');
        const orgs = response.data ?? [];
        setOrganizations(orgs);

        // Restore previously selected org from localStorage
        const storedOrgId = localStorage.getItem(STORAGE_KEY);
        if (storedOrgId && orgs.some((o) => o.id === storedOrgId)) {
          setCurrentOrgId(storedOrgId);
        } else if (orgs.length > 0) {
          // Default to first org if no stored selection
          setCurrentOrgId(orgs[0].id);
        }

        setError(null);
      } catch (err) {
        // If /orgs endpoint doesn't exist yet (pre-Phase 1), use mock data
        if (err instanceof Error && err.message.includes('404')) {
          setOrganizations([
            {
              id: 'default',
              name: 'Default Organization',
              slug: 'default',
              status: 'active',
            },
          ]);
          setCurrentOrgId('default');
        } else {
          const message = err instanceof Error ? err.message : 'Failed to load organizations';
          setError(message);
          // Surface error to user via toast
          showError('Failed to load organizations', message);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadOrgs();
  }, []);

  const switchOrg = useCallback(
    (orgId: string) => {
      const org = organizations.find((o) => o.id === orgId);

      // Capture previous state so we can roll back if the server rejects the
      // switch — never leave the UI claiming an org the server doesn't know.
      const previousOrgId = currentOrgId;
      const previousStored = localStorage.getItem(STORAGE_KEY);

      // Optimistic UI update — same behaviour as before so single-org users
      // and the pre-lane-3a-auth rollout see no regression.
      setCurrentOrgId(orgId);
      localStorage.setItem(STORAGE_KEY, orgId);

      // Tell Better Auth which org is now active and re-mint the JWT so the
      // next apiFetch carries the correct `activeOrganizationId` claim. The
      // outer caller fires-and-forgets (the OrgContext API stays sync); we
      // surface failure via toast + rollback inside this IIFE.
      void (async () => {
        const result = await setActiveOrganization(orgId);

        if (result.ok) {
          // Mint a fresh JWT so the new claim is in the Authorization header
          // on the next request. If the mint fails (network, build-ceo-auth down),
          // we still keep the optimistic localStorage — server-side
          // sessionAuth falls back to DB lookup, so requests will succeed,
          // just without the fresh claim until the next bootstrap.
          await fetchAndStoreBearerJwt();
          if (org) showSuccess(`Switched to ${org.name}`);
          return;
        }

        if (result.reason === 'unavailable') {
          // Graceful degradation: build-ceo-auth (lane 3a-auth) hasn't shipped the
          // BA organization plugin yet. Fall through to localStorage-only
          // behaviour — single-org users see no change.
          if (org) showSuccess(`Switched to ${org.name}`);
          return;
        }

        // Hard failure: roll back so the UI doesn't claim an org the server
        // doesn't know about, and surface the error via the existing toast.
        setCurrentOrgId(previousOrgId);
        if (previousStored) {
          localStorage.setItem(STORAGE_KEY, previousStored);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
        const message = result.message || `HTTP ${result.status ?? 'error'}`;
        showApiError(new Error(`Failed to switch organization: ${message}`));
      })();
    },
    [organizations, currentOrgId],
  );

  const currentOrg =
    currentOrgId ? organizations.find((o) => o.id === currentOrgId) ?? null : null;

  const value: OrgContextValue = {
    organizations,
    currentOrg,
    isLoading,
    error,
    switchOrg,
  };

  if (isLoading) return <LoadingScreen />;

  return createElement(OrgContext.Provider, { value }, children);
}
